import { db, firebase } from "../Config";

/**
 * Entidad Atencion en Firestore (Subcolección de Pacientes):
 * Path: pacientes/{pacienteId}/atenciones/{atencionId}
 * - especialistaId (string/email)
 * - empresaId (string)
 * - fechaInicio (Timestamp)
 * - fechaFin (Timestamp | null)
 * - estado (string: "ABIERTA" | "CERRADA")
 */

export const fetchAtencionActivaByUsuario = async (especialistaEmail) => {
    try {
        // Obtenemos la referencia desde el documento del usuario
        // Nota: asumiendo que el ID del documento en 'usuarios' es el UID que tenemos en la sesión
        // Si usamos email como ID de documento, cambia la referencia.
        // Buscaremos por query de email para ser más seguros según el código previo.
        const userSnapshot = await db.collection("usuarios").where("email", "==", especialistaEmail).limit(1).get();

        if (userSnapshot.empty) return null;
        const userData = userSnapshot.docs[0].data();

        if (!userData.atencion_activa) return null;

        const { id, pacienteId, empresaId } = userData.atencion_activa;

        // Recuperamos el detalle de la atencion desde la subcoleccion del paciente
        const atencionSnapshot = await db
            .collection("empresas")
            .doc(empresaId)
            .collection("pacientes")
            .doc(pacienteId)
            .collection("atenciones")
            .doc(id)
            .get();

        if (!atencionSnapshot.exists) return null;

        return { id: atencionSnapshot.id, ...atencionSnapshot.data() };
    } catch (error) {
        console.error("Error fetching active attendance by specialist:", error);
        throw error;
    }
};

export const fetchAtencionActivaByPaciente = async (pacienteId, empresaId) => {
    try {
        const snapshot = await db
            .collection("empresas")
            .doc(empresaId)
            .collection("pacientes")
            .doc(pacienteId)
            .collection("atenciones")
            .where("estado", "==", "ABIERTA")
            .limit(1)
            .get();

        if (snapshot.empty) return null;
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
    } catch (error) {
        console.error("Error fetching active attendance by patient:", error);
        throw error;
    }
};

export const iniciarAtencion = async (atencionData) => {
    const { pacienteId, especialistaId, empresaId } = atencionData;

    // 1. Validaciones
    const [atencionPaciente, atencionEspecialista] = await Promise.all([
        fetchAtencionActivaByPaciente(pacienteId, empresaId),
        fetchAtencionActivaByUsuario(especialistaId),
    ]);

    if (atencionPaciente) {
        throw new Error("El paciente ya tiene una atención abierta.");
    }

    if (atencionEspecialista) {
        throw new Error("Usted ya tiene una atención activa en este momento.");
    }

    // 2. Transacción Batch
    const batch = db.batch();

    // Nueva atencion en subcoleccion
    const atencionRef = db.collection("empresas").doc(empresaId).collection("pacientes").doc(pacienteId).collection("atenciones").doc();
    const nuevaAtencion = {
        pacienteId,
        especialistaId,
        empresaId,
        fechaInicio: atencionData.fechaInicio || new Date(),
        fechaFin: null,
        estado: "ABIERTA",
        items: [], // Initializing session items here
    };
    batch.set(atencionRef, nuevaAtencion);

    // Sync en documento de usuario
    const userQuery = await db.collection("usuarios").where("email", "==", especialistaId).limit(1).get();
    if (userQuery.empty) throw new Error("No se encontró el perfil del especialista.");

    const userDocRef = userQuery.docs[0].ref;
    batch.update(userDocRef, {
        atencion_activa: {
            id: atencionRef.id,
            pacienteId: pacienteId,
            empresaId: empresaId
        }
    });

    try {
        await batch.commit();
        return { id: atencionRef.id, ...nuevaAtencion };
    } catch (error) {
        console.error("Error starting clinical attendance in batch:", error);
        throw error;
    }
};

export const finalizarAtencion = async (atencionId, pacienteId, empresaId, especialistaEmail, estado = "CERRADA") => {
    try {
        const batch = db.batch();

        // 1. Cerrar atención en subcolección
        const atencionRef = db.collection("empresas").doc(empresaId).collection("pacientes").doc(pacienteId).collection("atenciones").doc(atencionId);
        batch.update(atencionRef, {
            fechaFin: firebase.firestore.FieldValue.serverTimestamp(),
            estado: estado,
        });

        // 2. Limpiar referencia en usuario
        const userQuery = await db.collection("usuarios").where("email", "==", especialistaEmail).limit(1).get();
        if (!userQuery.empty) {
            batch.update(userQuery.docs[0].ref, {
                atencion_activa: firebase.firestore.FieldValue.delete()
            });
        }

        await batch.commit();
        return true;
    } catch (error) {
        console.error("Error closing clinical attendance:", error);
        throw error;
    }
};

export const updateAtencionItems = async (pacienteId, atencionId, empresaId, items) => {
    try {
        await db.collection("empresas")
            .doc(empresaId)
            .collection("pacientes")
            .doc(pacienteId)
            .collection("atenciones")
            .doc(atencionId)
            .update({ items });
        return true;
    } catch (error) {
        console.error("Error updating atencion items:", error);
        throw error;
    }
};

// ... existing code ...

export const finalizarAtencionCompleta = async ({
    atencionId,
    pacienteId,
    empresaId,
    especialistaId,
    prestacionesToActivate,
    finalStatus,
    cuentaId
}) => {
    // Validaciones
    if (!atencionId || !pacienteId || !empresaId || !especialistaId || !cuentaId) {
        throw new Error("Datos incompletos para finalizar la atención.");
    }

    try {
        return await db.runTransaction(async (transaction) => {
            // 1. Obtener referencias
            const atencionRef = db.collection("empresas").doc(empresaId).collection("pacientes").doc(pacienteId).collection("atenciones").doc(atencionId);

            // Referencia de Cuenta
            const cuentaRef = db.collection("empresas").doc(empresaId).collection("pacientes").doc(pacienteId).collection("cuentas").doc(cuentaId);

            // Referencia de Usuario
            // Fetch User outside transaction or non-transactionally inside since we need query by email
            const userQuery = await db.collection("usuarios").where("email", "==", especialistaId).limit(1).get();
            const userRef = !userQuery.empty ? userQuery.docs[0].ref : null;

            // 2. Leer Cuenta Transactionally
            const cuentaDoc = await transaction.get(cuentaRef);
            if (!cuentaDoc.exists) {
                throw new Error("No se encontró la cuenta del paciente.");
            }
            const cuentaOne = cuentaDoc.data();

            let totalDeudaAdd = 0;
            const updatedPrestaciones = [];


            for (const prestacion of prestacionesToActivate) {
                const prestacionRef = db.collection("empresas").doc(empresaId).collection("pacientes").doc(pacienteId).collection("prestaciones").doc(prestacion.id);
                const movimientoRef = db.collection("empresas").doc(empresaId).collection("movimientos").doc();

                // Update Prestacion
                transaction.update(prestacionRef, {
                    estado: "ACTIVO",
                    precio: prestacion.precio,
                    notasClinicas: prestacion.notasClinicas || [],
                    nota: prestacion.nota || ""
                });

                // Create Movement
                const movimiento = {
                    fecha: firebase.firestore.FieldValue.serverTimestamp(),
                    monto: Number(prestacion.precio),
                    tipo: 'cargo_prestacion',
                    estado: 'completado',
                    prestacionId: prestacion.id,
                    cuentaId: cuentaDoc.id,
                    pacienteId: pacienteId,
                    usuario: especialistaId,
                    descripcion: `Activación de prestación: ${prestacion.tratamiento || 'Tratamiento'}`,
                    empresaId: empresaId
                };
                transaction.set(movimientoRef, movimiento);

                totalDeudaAdd += Number(prestacion.precio);

                updatedPrestaciones.push({
                    ...prestacion,
                    estado: "ACTIVO"
                });
            }

            // 3. Actualizar Cuenta
            if (totalDeudaAdd > 0) {
                transaction.update(cuentaRef, {
                    adeudado: firebase.firestore.FieldValue.increment(totalDeudaAdd)
                });
            }

            // 4. Cerrar Atención
            transaction.update(atencionRef, {
                fechaFin: firebase.firestore.FieldValue.serverTimestamp(),
                estado: finalStatus,
            });

            // 5. Actualizar Usuario
            if (userRef) {
                transaction.update(userRef, {
                    atencion_activa: firebase.firestore.FieldValue.delete()
                });
            }

            return {
                updatedPrestaciones,
                newCuentaData: {
                    ...cuentaOne,
                    adeudado: cuentaOne.adeudado + totalDeudaAdd
                }
            };
        });
    } catch (error) {
        console.error("Error en transacción de finalizar atención:", error);
        throw error;
    }
};

export const fetchAtencionesHistory = async (pacienteId, empresaId) => {
    try {
        const snapshot = await db.collection("empresas")
            .doc(empresaId)
            .collection("pacientes")
            .doc(pacienteId)
            .collection("atenciones")
            .orderBy("fechaInicio", "desc")
            .get();

        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            fechaInicio: doc.data().fechaInicio?.toDate ? doc.data().fechaInicio.toDate() : new Date(doc.data().fechaInicio?.seconds * 1000),
            fechaFin: doc.data().fechaFin?.toDate ? doc.data().fechaFin.toDate() : (doc.data().fechaFin ? new Date(doc.data().fechaFin.seconds * 1000) : null),
        }));
    } catch (error) {
        console.error("Error fetching atenciones history:", error);
        throw error;
    }
};
